import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { KuoriLogo } from "../../shared/logo.js";
import { Ring } from "../chrome/ring.js";
import {
  EVENT_LENGTH_MINUTES,
  remainingMinutes,
  ringLabel,
  sinceLabel,
  wallTime,
} from "../clock.js";
import { newestFirst, type ViewPhoto } from "../photos.js";
import { COVER_ID, DEMO_CODE, formatCode, seedSrc } from "../seed.js";
import type { DemoBrand } from "../types.js";

const TV_INSET = 40;

export interface WallScreenProps {
  readonly brand: DemoBrand;
  readonly logoUrl: string | null;
  readonly accent: string;
  readonly photos: readonly ViewPhoto[];
  readonly current: number;
}

interface SceneProps {
  readonly src: string;
  readonly title: string;
  readonly line: string;
}

function Scene({ src, title, line }: SceneProps) {
  const words = title.split(" ");
  const last = words.pop() ?? "";
  return (
    <figure class="d-wall-scene d-fade">
      <img src={src} alt="" />
      <span class="d-wall-veil" />
      <figcaption>
        <h2 class="d-wall-title">
          {words.join(" ")} <em>{last}</em>
        </h2>
        <p>{line}</p>
      </figcaption>
    </figure>
  );
}

export function WallScreen({ brand, logoUrl, accent, photos, current }: WallScreenProps) {
  const pool = newestFirst(photos).slice(0, 10);
  const newest = pool[0]?.id ?? "";
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [portrait, setPortrait] = useState(false);
  const [fullscreenUsed, setFullscreenUsed] = useState(() => !document.fullscreenEnabled);
  const frame = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRotation(0);
    if (pool.length < 4) {
      return;
    }
    let interval: ReturnType<typeof setInterval> | undefined;
    const idle = setTimeout(() => {
      interval = setInterval(() => setRotation((value) => value + 1), 12_000);
    }, 45_000);
    return () => {
      clearTimeout(idle);
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };
  }, [newest, pool.length]);

  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) {
      return;
    }
    const fit = () => {
      const upright = element.clientHeight > element.clientWidth;
      const value = upright
        ? (element.clientWidth - TV_INSET) / 960
        : Math.min(element.clientWidth / 960, element.clientHeight / 540);
      setPortrait(upright);
      setZoom(value > 0 ? value : 1);
    };
    fit();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "f") {
        return;
      }
      if (document.fullscreenElement) {
        void document.exitFullscreen?.().catch(() => undefined);
      } else {
        void document.documentElement.requestFullscreen?.().catch(() => undefined);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const layout = pool.length === 0 ? "empty" : pool.length < 4 ? "single" : "featured";
  const featured = pool[rotation % Math.max(pool.length, 1)];
  const recent = pool.slice(1, 7);
  const join = (
    <div class="d-wall-join">
      <img src="/assets/demo/qr.svg" alt="" width={80} height={80} />
      <p class="d-wall-code">{formatCode(DEMO_CODE)}</p>
      <p class="d-wall-join-caption">Entre no álbum</p>
    </div>
  );
  const remaining = remainingMinutes(current);

  return (
    <div
      class={portrait ? "d-screen d-wall-frame is-upright" : "d-screen d-wall-frame"}
      data-theme="dark"
      ref={frame}
      data-testid="screen-wall"
    >
      {portrait ? <p class="d-micro d-wall-tv-label">Na TV do salão</p> : null}
      <div class={portrait ? "d-wall-tv" : "d-wall-tv-off"} data-testid="wall-tv">
        <div class="d-wall-canvas" style={{ zoom: String(zoom) }}>
          <header class="d-wall-header">
            <div class="d-wall-host">
              {logoUrl ? (
                <img class="d-host-logo" src={logoUrl} alt={brand.name} />
              ) : (
                <span class="d-wall-name" data-testid="wall-host">
                  {brand.name}
                </span>
              )}
              <span class="d-live">ao vivo</span>
            </div>
            <div class="d-wall-stats">
              <div class="d-wall-count">
                <span class="d-micro">Fotos no álbum</span>
                <strong data-testid="wall-count">{photos.length}</strong>
              </div>
              <Ring size={48} stroke={3} fraction={remaining / EVENT_LENGTH_MINUTES} color={accent}>
                <span class="d-wall-ring-label">{ringLabel(remaining)}</span>
              </Ring>
            </div>
          </header>
          <div class="d-wall-stage" data-layout={layout} data-testid="wall-stage">
            {layout === "empty" ? (
              <Scene
                src={seedSrc(COVER_ID, "l")}
                title={brand.eventName}
                line="As primeiras fotos aparecem aqui"
              />
            ) : null}
            {layout === "single" && pool[0] ? (
              <Scene
                key={pool[0].id}
                src={pool[0].large}
                title={brand.eventName}
                line={`${pool[0].author} · ${wallTime(pool[0].at)}`}
              />
            ) : null}
            {layout === "featured" && featured ? (
              <>
                <figure
                  class="d-wall-featured d-fade"
                  key={featured.id}
                  data-testid="wall-featured"
                >
                  <img src={featured.large} alt="" />
                  <span class="d-wall-veil" />
                  <figcaption>
                    <span class="d-micro">{sinceLabel(featured.at, current)}</span>
                    <strong>{featured.author}</strong>
                  </figcaption>
                </figure>
                <div class="d-wall-side">
                  {recent.length > 0 ? (
                    <div class="d-wall-recent">
                      {recent.map((photo) => (
                        <img key={photo.id} class="d-wall-tile" src={photo.small} alt="" />
                      ))}
                    </div>
                  ) : null}
                  <div class="d-wall-panel">
                    <img src="/assets/demo/qr.svg" alt="" width={160} height={160} />
                    <div class="d-wall-panel-text">
                      <p class="d-wall-panel-code">{formatCode(DEMO_CODE)}</p>
                      <p class="d-wall-panel-caption">Entre no álbum e mande sua foto</p>
                      <p class="d-wall-panel-point">aponte a câmera</p>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          <footer class="d-wall-foot">
            feito com <KuoriLogo height={24} />
          </footer>
          {layout === "featured" ? null : join}
        </div>
      </div>
      {portrait ? (
        <p class="d-caption d-wall-tv-caption">Gire o celular para ver em tela cheia.</p>
      ) : null}
      {fullscreenUsed ? null : (
        <button
          type="button"
          class="d-wall-full"
          onClick={() => {
            setFullscreenUsed(true);
            void document.documentElement.requestFullscreen?.().catch(() => undefined);
          }}
        >
          Tela cheia
        </button>
      )}
    </div>
  );
}
